import { useWizardStore } from '@/store/wizardStore'
import type { MissionType } from '@/store/wizardStore'
import type { Lang } from '@/hooks/useLang'

interface Step3MissionsProps { lang: Lang }

const MISSIONS: {
  id: MissionType
  icon: string
  fr: { title: string; desc: string }
  en: { title: string; desc: string }
}[] = [
  {
    id: 'navigation',
    icon: 'explore',
    fr: { title: 'Navigation autonome', desc: 'Déplacement dans l\'espace sans intervention humaine.' },
    en: { title: 'Autonomous navigation', desc: 'Moving through space without human intervention.' },
  },
  {
    id: 'manipulation',
    icon: 'pan_tool',
    fr: { title: 'Manipulation d\'objets', desc: 'Saisir, déplacer et manipuler des éléments physiques.' },
    en: { title: 'Object manipulation', desc: 'Gripping, moving and handling physical items.' },
  },
  {
    id: 'speech_interaction',
    icon: 'record_voice_over',
    fr: { title: 'Interaction vocale', desc: 'Dialogue naturel avec des interlocuteurs humains.' },
    en: { title: 'Speech interaction', desc: 'Natural dialogue with human counterparts.' },
  },
  {
    id: 'surveillance',
    icon: 'videocam',
    fr: { title: 'Surveillance & monitoring', desc: 'Détection d\'anomalies, caméras HD, alerte temps réel.' },
    en: { title: 'Surveillance & monitoring', desc: 'Anomaly detection, HD cameras, real-time alerts.' },
  },
  {
    id: 'assistance',
    icon: 'support_agent',
    fr: { title: 'Assistance humaine', desc: 'Soutien direct aux personnes dans leurs activités quotidiennes.' },
    en: { title: 'Human assistance', desc: 'Direct support to people in their daily activities.' },
  },
  {
    id: 'transport',
    icon: 'local_shipping',
    fr: { title: 'Transport de charges', desc: 'Acheminement de matériaux ou colis sur un site.' },
    en: { title: 'Load transport', desc: 'Carrying materials or parcels across a site.' },
  },
  {
    id: 'inspection',
    icon: 'manage_search',
    fr: { title: 'Inspection technique', desc: 'Analyse visuelle ou sensorielle des équipements.' },
    en: { title: 'Technical inspection', desc: 'Visual or sensor-based equipment analysis.' },
  },
  {
    id: 'custom_task',
    icon: 'settings_suggest',
    fr: { title: 'Tâche sur mesure', desc: 'Mission spécifique définie selon votre cahier des charges.' },
    en: { title: 'Custom task', desc: 'Specific mission defined to your requirements.' },
  },
]

export default function Step3Missions({ lang }: Step3MissionsProps) {
  const { draft, update } = useWizardStore()
  const selected = draft.step3_missions ?? []

  function toggle(id: MissionType) {
    const next = selected.includes(id)
      ? selected.filter((m) => m !== id)
      : [...selected, id]
    update({ step3_missions: next })
  }

  const hint = lang === 'fr' ? 'Sélectionnez une ou plusieurs missions' : 'Select one or more missions'

  return (
    <div>
      <p className="text-on-surface-variant text-sm font-label tracking-wide mb-8">{hint}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {MISSIONS.map((m) => {
          const isSelected = selected.includes(m.id)
          return (
            <div
              key={m.id}
              onClick={() => toggle(m.id)}
              className={`glass-card p-6 rounded-xl border border-outline-variant/20 flex flex-col gap-3 cursor-pointer transition-all ${isSelected ? 'ring-1 ring-secondary bg-surface-container-highest/80' : ''}`}
            >
              <div className="flex items-center justify-between">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isSelected ? 'bg-secondary/20 text-secondary' : 'bg-surface-container-highest text-primary'}`}>
                  <span className="material-symbols-outlined text-xl">{m.icon}</span>
                </div>
                <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${isSelected ? 'bg-secondary border-secondary' : 'border-outline-variant/40'}`}>
                  {isSelected && <span className="material-symbols-outlined text-xs text-on-secondary">check</span>}
                </div>
              </div>
              <h4 className={`font-headline text-base font-bold transition-colors ${isSelected ? 'text-secondary' : 'text-on-surface'}`}>
                {m[lang].title}
              </h4>
              <p className="text-xs text-on-surface-variant leading-relaxed">{m[lang].desc}</p>
            </div>
          )
        })}
      </div>
    </div>
  )
}
